// src/components/manager/shared/AdaptableFormFields.jsx
import React, { useState } from 'react';
import { Plus, Minus, Upload, Calendar, MapPin, User, Building2, Award, Eye, Layout } from 'lucide-react';
import { FormField, TextInput, TextArea, SelectInput, Toggle } from '../../settings/shared/FormComponents';
import { ImageUpload } from '../../settings/shared/ImageUpload';
import { getFieldConfig } from '../../../services/templateAdaptationService';
import { firestoreService } from '../../../services/firestoreService';
import { FileUpload } from '../../common/FileUpload';

// NEW: Adaptable Hero Section Form
export const HeroSectionForm = ({ data, onChange, templateType, errors, bookId, onFileUpload }) => {
  const [uploadStates, setUploadStates] = useState({});
  const config = getFieldConfig('hero', templateType);
  
  // Fallback configuration when hero config doesn't exist
  const heroConfig = {
    title: 'Hero Section',
    subtitle: 'Create an impactful landing section for your organisation',
    headingLabel: 'Main Heading',
    headingPlaceholder: 'Welcome to Our Organisation',
    subHeadingLabel: 'Sub-heading',
    subHeadingPlaceholder: 'Your mission statement or tagline',
    descriptionLabel: 'Description',
    descriptionPlaceholder: 'Tell visitors about your organisation...',
    preHeadingLabel: 'Pre-heading (Optional)',
    preHeadingPlaceholder: 'Welcome visitors',
    primaryButtonTextLabel: 'Primary Button Text',
    primaryButtonTextPlaceholder: 'Get Started',
    primaryButtonUrlLabel: 'Primary Button URL',
    primaryButtonUrlPlaceholder: 'https://example.com',
    secondaryButtonTextLabel: 'Secondary Button Text',
    secondaryButtonTextPlaceholder: 'Learn More',
    secondaryButtonUrlLabel: 'Secondary Button URL',
    secondaryButtonUrlPlaceholder: 'https://example.com/about',
    videoUrlLabel: 'Video URL (Optional)',
    videoUrlPlaceholder: 'https://youtube.com/watch?v=...',
    layoutLabel: 'Layout Style',
    backgroundImageLabel: 'Background Image',
    backgroundImagePlaceholder: 'Upload a background image',
    mainImageLabel: 'Main Image',
    mainImagePlaceholder: 'Upload main section image',
    imageAltLabel: 'Image Alt Text',
    imageAltPlaceholder: 'Describe the image for accessibility',
    scrollIndicatorLabel: 'Show Scroll Indicator',
    enableCTA: true,
    enableSecondaryButton: true,
    enableVideoButton: true,
    enableBackgroundImage: true,
    enableMainImage: true,
    enableScrollIndicator: true,
    ...config // Override with template-specific config if available
  };

  const handleImageUpload = async (fieldName, file, error) => {
    if (error) {
      console.error(`Error selecting ${fieldName}:`, error);
      return;
    }

    if (!file) {
      onChange(fieldName, '');
      return;
    }

    if (!onFileUpload) {
      console.error('onFileUpload prop is missing');
      return;
    }

    setUploadStates(prev => ({ ...prev, [fieldName]: true }));

    try {
      // Use the onFileUpload prop passed from OrganisationManager
      const result = await onFileUpload(file, `hero/${fieldName}`);
      onChange(fieldName, result);
    } catch (uploadError) {
      console.error(`Error uploading ${fieldName}:`, uploadError);
    } finally {
      setUploadStates(prev => ({ ...prev, [fieldName]: false }));
    }
  };
  
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">{heroConfig.title}</h3>
        <span className="text-sm text-gray-500">{heroConfig.subtitle}</span>
      </div>

      {/* Content Section */}
      <div className="bg-gray-50 rounded-lg p-4">
        <h4 className="text-md font-medium text-gray-900 mb-4">Content</h4>
        
        <div className="space-y-4">
          <FormField label={heroConfig.preHeadingLabel}>
            <TextInput
              value={data.preHeading || ''}
              onChange={(value) => onChange('preHeading', value)}
              placeholder={heroConfig.preHeadingPlaceholder}
            />
          </FormField>

          <FormField label={heroConfig.headingLabel} required error={errors?.heading}>
            <TextInput
              value={data.heading || ''}
              onChange={(value) => onChange('heading', value)}
              placeholder={heroConfig.headingPlaceholder}
            />
          </FormField>

          <FormField label={heroConfig.subHeadingLabel}>
            <TextInput
              value={data.subHeading || ''}
              onChange={(value) => onChange('subHeading', value)}
              placeholder={heroConfig.subHeadingPlaceholder}
            />
          </FormField>

          <FormField label={heroConfig.descriptionLabel}>
            <TextArea
              value={data.description || ''}
              onChange={(value) => onChange('description', value)}
              placeholder={heroConfig.descriptionPlaceholder}
              rows={4}
            />
          </FormField>
        </div>
      </div>

      {/* Call to Action Buttons */}
      {heroConfig.enableCTA && (
        <div className="bg-blue-50 rounded-lg p-4">
          <h4 className="text-md font-medium text-gray-900 mb-4">Call to Action</h4>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField label={heroConfig.primaryButtonTextLabel}>
              <TextInput
                value={data.primaryButtonText || ''}
                onChange={(value) => onChange('primaryButtonText', value)}
                placeholder={heroConfig.primaryButtonTextPlaceholder}
              />
            </FormField>

            <FormField label={heroConfig.primaryButtonUrlLabel}>
              <TextInput
                value={data.primaryButtonUrl || ''}
                onChange={(value) => onChange('primaryButtonUrl', value)}
                placeholder={heroConfig.primaryButtonUrlPlaceholder}
                type="url"
              />
            </FormField>

            {heroConfig.enableSecondaryButton && (
              <>
                <FormField label={heroConfig.secondaryButtonTextLabel}>
                  <TextInput
                    value={data.secondaryButtonText || ''}
                    onChange={(value) => onChange('secondaryButtonText', value)}
                    placeholder={heroConfig.secondaryButtonTextPlaceholder}
                  />
                </FormField>

                <FormField label={heroConfig.secondaryButtonUrlLabel}>
                  <TextInput
                    value={data.secondaryButtonUrl || ''}
                    onChange={(value) => onChange('secondaryButtonUrl', value)}
                    placeholder={heroConfig.secondaryButtonUrlPlaceholder}
                    type="url"
                  />
                </FormField>
              </>
            )}
          </div>

          {heroConfig.enableVideoButton && (
            <FormField label={heroConfig.videoUrlLabel}>
              <TextInput
                value={data.videoUrl || ''}
                onChange={(value) => onChange('videoUrl', value)}
                placeholder={heroConfig.videoUrlPlaceholder}
                type="url"
              />
            </FormField>
          )}
        </div>
      )}

      {/* Layout and Visual Settings */}
      <div className="bg-green-50 rounded-lg p-4">
        <h4 className="text-md font-medium text-gray-900 mb-4">Layout & Visual</h4>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField label={heroConfig.layoutLabel}>
            <SelectInput
              value={data.layout || 'center'}
              onChange={(value) => onChange('layout', value)}
              options={[
                { value: 'center', label: 'Center Aligned' },
                { value: 'split', label: 'Split Layout' },
                { value: 'left', label: 'Left Aligned' }
              ]}
            />
          </FormField>

          {heroConfig.enableScrollIndicator && (
            <FormField label={heroConfig.scrollIndicatorLabel}>
              <Toggle
                checked={data.showScrollIndicator || false}
                onChange={(value) => onChange('showScrollIndicator', value)}
                label="Show scroll down indicator"
              />
            </FormField>
          )}
        </div>

        {/* Background Image */}
        {heroConfig.enableBackgroundImage && (
          <FormField label={heroConfig.backgroundImageLabel}>
            <FileUpload
              label="Upload Background Image"
              currentFile={data.backgroundImage}
              onFileSelect={(file, error) => handleImageUpload('backgroundImage', file, error)}
              onRemove={() => onChange('backgroundImage', '')}
              accept="image/*"
              maxSize={5}
              uploading={uploadStates.backgroundImage}
              showPreview={true}
            />
          </FormField>
        )}

        {/* Main Image (for split layout) */}
        {heroConfig.enableMainImage && data.layout === 'split' && (
          <div className="mt-4">
            <FormField label={heroConfig.mainImageLabel}>
              <FileUpload
                label="Upload Main Image"
                currentFile={data.image}
                onFileSelect={(file, error) => handleImageUpload('image', file, error)}
                onRemove={() => onChange('image', '')}
                accept="image/*"
                maxSize={3}
                uploading={uploadStates.image}
                showPreview={true}
              />
            </FormField>
            
            {data.image && (
              <FormField label={heroConfig.imageAltLabel} className="mt-2">
                <TextInput
                  value={data.imageAlt || ''}
                  onChange={(value) => onChange('imageAlt', value)}
                  placeholder={heroConfig.imageAltPlaceholder}
                />
              </FormField>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// Adaptable About Section Form
export const AboutSectionForm = ({ data, onChange, templateType, errors, bookId }) => {
  const [uploadStates, setUploadStates] = useState({});
  const config = getFieldConfig('about', templateType);
  
  // Enhanced config with image options
  const aboutConfig = {
    title: 'About Section',
    subtitle: 'Tell your story with compelling content and imagery',
    descriptionLabel: 'Description',
    descriptionPlaceholder: 'Tell your story...',
    imageLabel: 'Section Image',
    imageAltLabel: 'Image Alt Text',
    imageAltPlaceholder: 'Describe the image for accessibility',
    enableImage: true,
    enableImageStyling: true,
    enableImagePlacement: true,
    ...config
  };

  // Handle image upload
  const handleImageUpload = async (fieldName, file, error) => {
    if (error) {
      console.error(`Error selecting ${fieldName}:`, error);
      setUploadStates(prev => ({ ...prev, [fieldName]: { error: error.message } }));
      return;
    }

    if (!file || !bookId) return;

    try {
      setUploadStates(prev => ({ ...prev, [fieldName]: { uploading: true } }));
      
      const storagePath = `pb-${bookId}/about/${fieldName}`;
      const result = await firestoreService.uploadFile(file, storagePath);
      
      onChange(fieldName, result.url);
      setUploadStates(prev => ({ ...prev, [fieldName]: { uploading: false } }));
      
    } catch (uploadError) {
      console.error(`Error uploading ${fieldName}:`, uploadError);
      setUploadStates(prev => ({ 
        ...prev, 
        [fieldName]: { uploading: false, error: uploadError.message } 
      }));
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">{aboutConfig.title}</h3>
        <span className="text-sm text-gray-500">{aboutConfig.subtitle}</span>
      </div>

      {/* Description */}
      <FormField label={aboutConfig.descriptionLabel} required error={errors?.description}>
        <TextArea
          value={data.description || ''}
          onChange={(value) => onChange('description', value)}
          placeholder={aboutConfig.descriptionPlaceholder}
          rows={4}
        />
      </FormField>

      {/* Layout Options */}
      <FormField label="Layout Style">
        <SelectInput
          value={data.layout || 'single'}
          onChange={(value) => onChange('layout', value)}
          options={[
            { value: 'single', label: 'Single Column' },
            { value: 'split', label: 'Split Layout' },
            { value: 'image-left', label: 'Image Left' },
            { value: 'image-right', label: 'Image Right' }
          ]}
        />
      </FormField>

      {/* Image Upload Section */}
      {aboutConfig.enableImage && (
        <div className="space-y-4 border-t pt-6">
          <h4 className="text-md font-semibold text-gray-900 flex items-center gap-2">
            <Layout className="h-4 w-4" />
            Image Settings
          </h4>

          {/* Image Upload */}
          <FormField label={aboutConfig.imageLabel}>
            <FileUpload
              label="Upload Section Image"
              currentFile={data.image}
              onFileSelect={(file, error) => handleImageUpload('image', file, error)}
              onRemove={() => onChange('image', '')}
              accept="image/*"
              maxSize={5}
              uploading={uploadStates.image?.uploading}
              error={uploadStates.image?.error}
              showPreview={true}
            />
          </FormField>

          {/* Image Alt Text */}
          {data.image && (
            <FormField label={aboutConfig.imageAltLabel}>
              <TextInput
                value={data.imageAlt || ''}
                onChange={(value) => onChange('imageAlt', value)}
                placeholder={aboutConfig.imageAltPlaceholder}
              />
            </FormField>
          )}

          {/* Image Styling Options */}
          {data.image && aboutConfig.enableImageStyling && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              
              {/* Image Placement */}
              <FormField label="Image Position">
                <SelectInput
                  value={data.imagePosition || 'left'}
                  onChange={(value) => onChange('imagePosition', value)}
                  options={[
                    { value: 'left', label: 'Left Side' },
                    { value: 'right', label: 'Right Side' },
                    { value: 'top', label: 'Above Text' },
                    { value: 'bottom', label: 'Below Text' }
                  ]}
                />
              </FormField>

              {/* Aspect Ratio */}
              <FormField label="Aspect Ratio">
                <SelectInput
                  value={data.imageAspectRatio || 'auto'}
                  onChange={(value) => onChange('imageAspectRatio', value)}
                  options={[
                    { value: 'auto', label: 'Auto' },
                    { value: '16:9', label: '16:9 (Widescreen)' },
                    { value: '4:3', label: '4:3 (Standard)' },
                    { value: '1:1', label: '1:1 (Square)' },
                    { value: '3:2', label: '3:2 (Photo)' },
                    { value: '3:4', label: '3:4 (Portrait)' }
                  ]}
                />
              </FormField>

              {/* Image Height */}
              <FormField label="Image Height">
                <SelectInput
                  value={data.imageHeight || 'medium'}
                  onChange={(value) => onChange('imageHeight', value)}
                  options={[
                    { value: 'small', label: 'Small (200px)' },
                    { value: 'medium', label: 'Medium (300px)' },
                    { value: 'large', label: 'Large (400px)' },
                    { value: 'extra-large', label: 'Extra Large (500px)' },
                    { value: 'full', label: 'Full Container' }
                  ]}
                />
              </FormField>

              {/* Corner Radius */}
              <FormField label="Corner Style">
                <SelectInput
                  value={data.imageCorners || 'rounded'}
                  onChange={(value) => onChange('imageCorners', value)}
                  options={[
                    { value: 'none', label: 'Square Corners' },
                    { value: 'rounded', label: 'Rounded' },
                    { value: 'rounded-lg', label: 'Large Rounded' },
                    { value: 'rounded-xl', label: 'Extra Large Rounded' },
                    { value: 'rounded-full', label: 'Fully Rounded' }
                  ]}
                />
              </FormField>
            </div>
          )}

          {/* Shadow and Effects */}
          {data.image && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              
              {/* Shadow Style */}
              <FormField label="Shadow Effect">
                <SelectInput
                  value={data.imageShadow || 'none'}
                  onChange={(value) => onChange('imageShadow', value)}
                  options={[
                    { value: 'none', label: 'No Shadow' },
                    { value: 'sm', label: 'Small Shadow' },
                    { value: 'md', label: 'Medium Shadow' },
                    { value: 'lg', label: 'Large Shadow' },
                    { value: 'xl', label: 'Extra Large Shadow' },
                    { value: '2xl', label: 'Dramatic Shadow' }
                  ]}
                />
              </FormField>

              {/* Hover Effects */}
              <FormField label="Hover Effect">
                <SelectInput
                  value={data.imageHoverEffect || 'none'}
                  onChange={(value) => onChange('imageHoverEffect', value)}
                  options={[
                    { value: 'none', label: 'No Effect' },
                    { value: 'scale', label: 'Scale Up' },
                    { value: 'lift', label: 'Lift (Increase Shadow)' },
                    { value: 'fade', label: 'Fade' },
                    { value: 'brightness', label: 'Brighten' }
                  ]}
                />
              </FormField>
            </div>
          )}

          {/* Image Alignment within Container */}
          {data.image && (
            <FormField label="Image Alignment">
              <SelectInput
                value={data.imageAlignment || 'center'}
                onChange={(value) => onChange('imageAlignment', value)}
                options={[
                  { value: 'left', label: 'Align Left' },
                  { value: 'center', label: 'Center' },
                  { value: 'right', label: 'Align Right' }
                ]}
              />
            </FormField>
          )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
          <FormField label="Section Variant">
            <SelectInput
              value={data.ui?.variant || 'classic'} // classic | modern | spotlight
              onChange={(v) => onChange('ui', { ...(data.ui||{}), variant: v })}
              options={[
                { value: 'classic', label: 'Classic' },
                { value: 'modern', label: 'Modern (cards + accents)' },
                { value: 'spotlight', label: 'Spotlight (big image hero)' },
              ]}
            />
          </FormField>

          <FormField label="Background Style">
            <SelectInput
              value={data.ui?.background || 'plain'} // plain | soft | gradient | pattern
              onChange={(v) => onChange('ui', { ...(data.ui||{}), background: v })}
              options={[
                { value: 'plain', label: 'Plain' },
                { value: 'soft', label: 'Soft Tint' },
                { value: 'gradient', label: 'Gradient' },
                { value: 'pattern', label: 'Pattern (dots/diagonal)' },
              ]}
            />
          </FormField>

          {/* Gradient colours if background === 'gradient' */}
          {data.ui?.background === 'gradient' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField label="Gradient From">
                <TextInput
                  value={data.ui?.gradientFrom || ''}
                  onChange={(v) => onChange('ui', { ...(data.ui||{}), gradientFrom: v })}
                  placeholder="#0795ff"
                />
              </FormField>
              <FormField label="Gradient To">
                <TextInput
                  value={data.ui?.gradientTo || ''}
                  onChange={(v) => onChange('ui', { ...(data.ui||{}), gradientTo: v })}
                  placeholder="#6ee7b7"
                />
              </FormField>
            </div>
          )}

          <FormField label="Heading Style">
            <SelectInput
              value={data.ui?.headingStyle || 'label-divider'} // simple | label-divider | centered-badge
              onChange={(v) => onChange('ui', { ...(data.ui||{}), headingStyle: v })}
              options={[
                { value: 'simple', label: 'Simple' },
                { value: 'label-divider', label: 'Label + Divider' },
                { value: 'centered-badge', label: 'Centered Badge' },
              ]}
            />
          </FormField>

          <FormField label="Card Style">
            <SelectInput
              value={data.ui?.cardStyle || 'elevated'} // elevated | glass | outline
              onChange={(v) => onChange('ui', { ...(data.ui||{}), cardStyle: v })}
              options={[
                { value: 'elevated', label: 'Elevated' },
                { value: 'glass', label: 'Glass (frosted)' },
                { value: 'outline', label: 'Outline' },
              ]}
            />
          </FormField>

          <FormField label="Icon Style">
            <SelectInput
              value={data.ui?.iconStyle || 'circle'} // circle | soft | none
              onChange={(v) => onChange('ui', { ...(data.ui||{}), iconStyle: v })}
              options={[
                { value: 'circle', label: 'Coloured Circle' },
                { value: 'soft', label: 'Soft Badge' },
                { value: 'none', label: 'No Badge' },
              ]}
            />
          </FormField>

          <FormField label="Mission/Vision Display">
            <SelectInput
              value={data.ui?.mvMode || 'split'} // split | flip | tabs | stack
              onChange={(v) => onChange('ui', { ...(data.ui||{}), mvMode: v })}
              options={[
                { value: 'split', label: 'Side by Side' },
                { value: 'flip', label: 'Flip Card' },
                { value: 'tabs', label: 'Tabs' },
                { value: 'stack', label: 'Stacked' },
              ]}
            />
          </FormField>

          <FormField label="Values Display">
            <SelectInput
              value={data.ui?.valuesMode || 'grid'} // grid | list | chips
              onChange={(v) => onChange('ui', { ...(data.ui||{}), valuesMode: v })}
              options={[
                { value: 'grid', label: 'Icon Grid' },
                { value: 'list', label: 'List' },
                { value: 'chips', label: 'Chips' },
              ]}
            />
          </FormField>

          <FormField label="History Display">
            <SelectInput
              value={data.ui?.historyMode || 'paragraph'} // paragraph | timeline
              onChange={(v) => onChange('ui', { ...(data.ui||{}), historyMode: v })}
              options={[
                { value: 'paragraph', label: 'Paragraph' },
                { value: 'timeline', label: 'Timeline' },
              ]}
            />
          </FormField>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <FormField label="Enable Animations">
              <Toggle
                checked={!!data.ui?.animate}
                onChange={(checked) => onChange('ui', { ...(data.ui||{}), animate: checked })}
              />
            </FormField>
          </div>
            <FormField label="Animation Type">
              <SelectInput
                value={data.ui?.animationType || 'fade'} // fade | slide | zoom
                onChange={(v) => onChange('ui', { ...(data.ui||{}), animationType: v })}
                options={[
                  { value: 'fade', label: 'Fade' },
                  { value: 'slide', label: 'Slide' },
                  { value: 'zoom', label: 'Zoom' },
                ]}
              />
            </FormField>
            <FormField label="Accent Colour (optional)">
              <TextInput
                value={data.ui?.accent || ''}
                onChange={(v) => onChange('ui', { ...(data.ui||{}), accent: v })}
                placeholder="e.g. #190192 or leave empty"
              />
            </FormField>
          </div>
        </div>
      )}
    </div>
  );
};

// Adaptable Leadership/Team Form
export const LeadershipSectionForm = ({ data, onChange, templateType, errors }) => {
  const config = getFieldConfig('leadership', templateType);
  
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">{config.title}</h3>
        <span className="text-sm text-gray-500">{config.subtitle}</span>
      </div>

      <FormField label={config.listLabel}>
        <DynamicListInput
          value={data.members || []}
          onChange={(value) => onChange('members', value)}
          itemTemplate={{
            name: { label: 'Name', placeholder: 'Full name', required: true },
            position: { label: config.positionLabel, placeholder: config.positionPlaceholder },
            bio: { label: 'Biography', placeholder: 'Brief background...', type: 'textarea' },
            image: { label: 'Photo', type: 'image' },
            email: { label: 'Email', placeholder: 'email@example.com', type: 'email' },
            phone: { label: 'Phone', placeholder: '+27 XX XXX XXXX', type: 'tel' },
            ...(config.enableSocial && {
              linkedin: { label: 'LinkedIn', placeholder: 'LinkedIn URL' },
              twitter: { label: 'Twitter', placeholder: 'Twitter handle' }
            }),
            ...(config.enableQualifications && {
              qualifications: { label: config.qualificationsLabel, placeholder: 'Degrees, certifications...', type: 'textarea' }
            })
          }}
          addLabel={`Add ${config.memberLabel}`}
        />
      </FormField>

      {config.enableDepartments && (
        <FormField label={config.departmentsLabel}>
          <DynamicListInput
            value={data.departments || []}
            onChange={(value) => onChange('departments', value)}
            itemTemplate={{
              name: { label: 'Department Name', placeholder: config.departmentPlaceholder },
              head: { label: 'Department Head', placeholder: 'Head of department...' },
              description: { label: 'Description', placeholder: 'Department description...', type: 'textarea' }
            }}
            addLabel="Add Department"
          />
        </FormField>
      )}
    </div>
  );
};

// Adaptable Services Form
export const ServicesSectionForm = ({ data, onChange, templateType, errors }) => {
  const config = getFieldConfig('services', templateType);
  
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">{config.title}</h3>
        <span className="text-sm text-gray-500">{config.subtitle}</span>
      </div>

      <FormField label={config.listLabel}>
        <DynamicListInput
          value={data.services || []}
          onChange={(value) => onChange('services', value)}
          itemTemplate={{
            title: { label: 'Service Title', placeholder: config.titlePlaceholder, required: true },
            description: { label: 'Description', placeholder: config.descriptionPlaceholder, type: 'textarea' },
            image: { label: 'Service Image', type: 'image' },
            ...(config.enablePricing && {
              price: { label: 'Price', type: 'number', placeholder: 'R 0.00' },
              currency: { label: 'Currency', type: 'select', options: [
                { value: 'ZAR', label: 'ZAR (R)' },
                { value: 'USD', label: 'USD ($)' }
              ]}
            }),
            category: { label: config.categoryLabel, placeholder: config.categoryPlaceholder },
            tags: { label: 'Tags', placeholder: 'Comma-separated tags' },
            ...(config.enableFeatures && {
              features: { label: 'Features/Benefits', placeholder: 'Key features (one per line)', type: 'textarea' }
            })
          }}
          addLabel={`Add ${config.itemLabel}`}
        />
      </FormField>

      {config.enableCategories && (
        <FormField label={config.categoriesLabel}>
          <DynamicListInput
            value={data.categories || []}
            onChange={(value) => onChange('categories', value)}
            itemTemplate={{
              name: { label: 'Category Name', placeholder: config.categoryPlaceholder },
              description: { label: 'Description', placeholder: 'Category description...' }
            }}
            addLabel="Add Category"
          />
        </FormField>
      )}
    </div>
  );
};

// Adaptable Events Form
export const EventsSectionForm = ({ data, onChange, templateType, errors }) => {
  const config = getFieldConfig('events', templateType);
  
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">{config.title}</h3>
        <span className="text-sm text-gray-500">{config.subtitle}</span>
      </div>

      <FormField label={config.listLabel}>
        <DynamicListInput
          value={data.events || []}
          onChange={(value) => onChange('events', value)}
          itemTemplate={{
            title: { label: 'Event Title', placeholder: config.titlePlaceholder, required: true },
            description: { label: 'Description', placeholder: config.descriptionPlaceholder, type: 'textarea' },
            date: { label: 'Date', type: 'date', required: true },
            time: { label: 'Time', type: 'time' },
            location: { label: 'Location', placeholder: config.locationPlaceholder },
            image: { label: 'Event Image', type: 'image' },
            ...(config.enableRegistration && {
              enableRegistration: { label: 'Enable Registration', type: 'toggle' },
              registrationDeadline: { label: 'Registration Deadline', type: 'date' },
              maxAttendees: { label: 'Max Attendees', type: 'number', placeholder: 'Leave empty for unlimited' }
            }),
            ...(config.enablePricing && {
              price: { label: 'Price', type: 'number', placeholder: 'R 0.00' },
              currency: { label: 'Currency', type: 'select', options: [
                { value: 'ZAR', label: 'ZAR (R)' },
                { value: 'USD', label: 'USD ($)' }
              ]}
            }),
            category: { label: config.categoryLabel, placeholder: config.categoryPlaceholder },
            tags: { label: 'Tags', placeholder: 'Comma-separated tags' }
          }}
          addLabel={`Add ${config.itemLabel}`}
        />
      </FormField>
    </div>
  );
};

// Adaptable News/Updates Form
export const NewsSectionForm = ({ data, onChange, templateType, errors }) => {
  const config = getFieldConfig('news', templateType);
  
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">{config.title}</h3>
        <span className="text-sm text-gray-500">{config.subtitle}</span>
      </div>

      <FormField label={config.listLabel}>
        <DynamicListInput
          value={data.articles || []}
          onChange={(value) => onChange('articles', value)}
          itemTemplate={{
            title: { label: 'Title', placeholder: config.titlePlaceholder, required: true },
            summary: { label: 'Summary', placeholder: config.summaryPlaceholder },
            content: { label: 'Content', placeholder: config.contentPlaceholder, type: 'textarea' },
            image: { label: 'Featured Image', type: 'image' },
            date: { label: 'Publication Date', type: 'date', required: true },
            author: { label: 'Author', placeholder: config.authorPlaceholder },
            category: { label: config.categoryLabel, placeholder: config.categoryPlaceholder },
            tags: { label: 'Tags', placeholder: 'Comma-separated tags' },
            ...(config.enableStatus && {
              status: { label: 'Status', type: 'select', options: [
                { value: 'draft', label: 'Draft' },
                { value: 'published', label: 'Published' },
                { value: 'archived', label: 'Archived' }
              ]}
            })
          }}
          addLabel={`Add ${config.itemLabel}`}
        />
      </FormField>
    </div>
  );
};

// Adaptable Contact Form
export const ContactSectionForm = ({ data, onChange, templateType, errors }) => {
  const config = getFieldConfig('contact', templateType);
  
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">{config.title}</h3>
        <span className="text-sm text-gray-500">{config.subtitle}</span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField label={config.phoneLabel} error={errors?.phone}>
          <TextInput
            value={data.phone || ''}
            onChange={(value) => onChange('phone', value)}
            placeholder={config.phonePlaceholder}
            type="tel"
          />
        </FormField>

        <FormField label={config.emailLabel} required error={errors?.email}>
          <TextInput
            value={data.email || ''}
            onChange={(value) => onChange('email', value)}
            placeholder={config.emailPlaceholder}
            type="email"
          />
        </FormField>
      </div>

      <FormField label={config.addressLabel}>
        <TextArea
          value={data.address || ''}
          onChange={(value) => onChange('address', value)}
          placeholder={config.addressPlaceholder}
          rows={3}
        />
      </FormField>

      {config.enableBusinessHours && (
        <FormField label={config.businessHoursLabel}>
          <DynamicListInput
            value={data.businessHours || []}
            onChange={(value) => onChange('businessHours', value)}
            itemTemplate={{
              days: { label: 'Days', placeholder: 'Monday - Friday' },
              open: { label: 'Opening Time', type: 'time' },
              close: { label: 'Closing Time', type: 'time' },
              closed: { label: 'Closed', type: 'toggle' }
            }}
            addLabel="Add Hours"
          />
        </FormField>
      )}

      {config.enableContactForm && (
        <FormField label={config.contactFormLabel}>
          <Toggle
            value={data.showForm || false}
            onChange={(value) => onChange('showForm', value)}
            label={config.contactFormToggleLabel}
          />
        </FormField>
      )}

      {config.enableMap && (
        <FormField label={config.mapLabel}>
          <TextInput
            value={data.mapUrl || ''}
            onChange={(value) => onChange('mapUrl', value)}
            placeholder={config.mapPlaceholder}
            type="url"
          />
        </FormField>
      )}
    </div>
  );
};

// Dynamic List Input Component (helper component for complex list inputs)
export const DynamicListInput = ({ value = [], onChange, itemTemplate, addLabel = 'Add Item' }) => {
  const addItem = () => {
    const newItem = {};
    Object.keys(itemTemplate).forEach(key => {
      const field = itemTemplate[key];
      newItem[key] = field.type === 'toggle' ? false : '';
    });
    onChange([...value, newItem]);
  };

  const updateItem = (index, field, fieldValue) => {
    const updatedItems = value.map((item, i) => 
      i === index ? { ...item, [field]: fieldValue } : item
    );
    onChange(updatedItems);
  };

  const removeItem = (index) => {
    onChange(value.filter((_, i) => i !== index));
  };

  return (
    <div className="space-y-4">
      {value.map((item, index) => (
        <div key={index} className="border border-gray-200 rounded-lg p-4">
          <div className="flex justify-between items-start mb-3">
            <span className="text-sm font-medium text-gray-700">Item {index + 1}</span>
            <button
              type="button"
              onClick={() => removeItem(index)}
              className="text-red-600 hover:text-red-800"
            >
              <Minus className="h-4 w-4" />
            </button>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {Object.entries(itemTemplate).map(([fieldKey, fieldConfig]) => (
              <FormField 
                key={fieldKey} 
                label={fieldConfig.label} 
                required={fieldConfig.required}
              >
                {fieldConfig.type === 'textarea' ? (
                  <TextArea
                    value={item[fieldKey] || ''}
                    onChange={(val) => updateItem(index, fieldKey, val)}
                    placeholder={fieldConfig.placeholder}
                    rows={3}
                  />
                ) : fieldConfig.type === 'select' ? (
                  <SelectInput
                    value={item[fieldKey] || ''}
                    onChange={(val) => updateItem(index, fieldKey, val)}
                    options={fieldConfig.options}
                  />
                ) : fieldConfig.type === 'toggle' ? (
                  <Toggle
                    value={item[fieldKey] || false}
                    onChange={(val) => updateItem(index, fieldKey, val)}
                    label={fieldConfig.label}
                  />
                ) : fieldConfig.type === 'image' ? (
                  <ImageUpload
                    value={item[fieldKey] || ''}
                    onChange={(val) => updateItem(index, fieldKey, val)}
                    placeholder={fieldConfig.placeholder}
                    maxSize={2}
                    folder={fieldKey}
                  />
                ) : (
                  <TextInput
                    value={item[fieldKey] || ''}
                    onChange={(val) => updateItem(index, fieldKey, val)}
                    placeholder={fieldConfig.placeholder}
                    type={fieldConfig.type || 'text'}
                  />
                )}
              </FormField>
            ))}
          </div>
        </div>
      ))}
      
      <button
        type="button"
        onClick={addItem}
        className="w-full flex items-center justify-center gap-2 px-4 py-2 border border-dashed border-gray-300 rounded-lg text-gray-600 hover:text-gray-900 hover:border-gray-400"
      >
        <Plus className="h-4 w-4" />
        {addLabel}
      </button>
    </div>
  );
};